import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { INDICATION_TYPES } from 'taboola-ultimate-ui';
import { FORM_MODES } from 'config/formModes';
import { useNavigate } from 'hooks';
import { generateCampaignsGroupEditorPath } from 'modules/campaigns/services/utils';
import { addIndication } from 'modules/taboola-common-frontend-modules/Indications';
import { usePermissions } from 'modules/taboola-common-frontend-modules/authentication/hooks';
import { FormattedMessage } from 'modules/taboola-common-frontend-modules/i18n';
import { selectedAccountSelector } from 'selectors';
import { isCampaignGroupDeleted } from '../../campaigns-reports/config/predicates/campaignGroupPredicates';

export const previewIndication = {
    message: (
        <FormattedMessage
            id="campaigns.group.editor.view.only.access"
            defaultMessage="Campaign Group edit is forbidden by user permission."
        />
    ),
    type: INDICATION_TYPES.WARNING,
    highlight: <FormattedMessage id="note.highlight" />,
};

const getPreviewIndication = ({ hasEditAccess }) => {
    if (!hasEditAccess) {
        return previewIndication;
    }
};

const useOpenCampaignsGroupEditor = campaignGroup => {
    const dispatch = useDispatch();

    const { accountId: selectedAccountId, isActive: isActiveAccount } = useSelector(selectedAccountSelector);
    const hasEditAccess = usePermissions('EDIT_CAMPAIGN_GROUP');
    const navigate = useNavigate();
    return useCallback(
        campaignsGroup => {
            const { campaignsGroupId } = campaignsGroup;
            const mode =
                isActiveAccount && hasEditAccess && !isCampaignGroupDeleted(campaignGroup)
                    ? FORM_MODES.EDIT
                    : FORM_MODES.PREVIEW;
            const editCampaignsGroupPath = generateCampaignsGroupEditorPath({
                accountId: selectedAccountId,
                campaignsGroupId,
                mode,
            });

            navigate(editCampaignsGroupPath);
            if (mode === FORM_MODES.PREVIEW) {
                const indication = getPreviewIndication({
                    hasEditAccess,
                });
                if (indication) {
                    dispatch(addIndication(indication));
                }
            }
            navigate(editCampaignsGroupPath);
        },
        [selectedAccountId, isActiveAccount, hasEditAccess, campaignGroup, navigate, dispatch]
    );
};

export default useOpenCampaignsGroupEditor;
